<template>
  <div class="navbar_component">
    <!-- Desktop Navbar -->
    <div class="navbar_wrap" :class="{ scrolled: isScrolled }">
      <div class="navbar_container">
        <div class="navbar_left">
          <img :src="$brand.logo" :alt="$t('brand.logoAlt')" @click="goToHome" />
          <div class="comName">{{ $t('company.name') }}</div>
        </div>
        <div class="navbar_right">
          <el-menu
            :default-active="activeIndex"
            class="el_menu_demo"
            mode="horizontal"
            @select="changeCurrentPage"
          >
            <el-menu-item index="1" class="menu_item">{{ $t('navbar.Index') }}</el-menu-item>
            <el-menu-item index="8" class="menu_item">{{ $t('navbar.Company') }}</el-menu-item>
            <el-menu-item index="2-1" class="menu_item">{{ $t('navbar.Products') }}</el-menu-item>
            <el-menu-item index="3" class="menu_item">{{ $t('navbar.News') }}</el-menu-item>
            <el-menu-item index="7" class="menu_item">{{ $t('navbar.FAQ') }}</el-menu-item>
            <el-submenu index="5">
              <template slot="title">
                <i class="language-flag">{{ currentLanguageFlag }}</i>
                {{ currentLanguageLabel }}
              </template>
              <el-menu-item 
                v-for="lang in availableLanguages" 
                :key="lang.code" 
                :index="`lang-${lang.code}`" 
                class="menu_item language-item"
                :class="{ active: currentLanguage === lang.code }"
                @click="changeLanguage(lang.code)"
              >
                <i class="language-flag">{{ getLanguageFlag(lang.code) }}</i>
                {{ lang.name }}
              </el-menu-item>
            </el-submenu>
          </el-menu>

          <!-- 登录 / 退出操作区 -->
          <div class="auth_actions">
            <button
              v-if="!isLoggedIn"
              class="auth_btn auth_btn--login"
              @click="goToLogin"
            >
              <i class="el-icon-user"></i>
              <span>{{ $t('navbar.login') }}</span>
            </button>
            <template v-else>
              <button class="auth_btn auth_btn--control" @click="goToControlCenter">
                <i class="el-icon-s-grid"></i>
                <span>{{ $t('navbar.controlCenter') }}</span>
              </button>
              <button
                class="auth_btn auth_btn--logout"
                :title="$t('navbar.logout')"
                @click="handleLogout"
              >
                <i class="el-icon-switch-button"></i>
                <span>{{ $t('navbar.logout') }}</span>
              </button>
            </template>
          </div>

        </div>
      </div>
    </div>

    <!-- Mobile Navbar -->
    <div class="mobile_navbar" :class="{ scrolled: isScrolled }">
      <div class="mobile_navbar_container">
        <div class="mobile_navbar_left">
          <img :src="$brand.logo" :alt="$t('brand.logoAlt')" @click="goToHome" />
          <div class="mobile_comName">{{ $t('company.name') }}</div>
        </div>
        <div class="mobile_navbar_right">
          <button class="hamburger_btn" @click="toggleMobileMenu" :class="{ active: isMobileMenuOpen }">
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </div>

      <!-- Mobile Menu Overlay -->
      <transition name="mobile-menu">
        <div v-if="isMobileMenuOpen" class="mobile_menu_overlay" @click="closeMobileMenu">
          <div class="mobile_menu" @click.stop>
            <div class="mobile_menu_header">
              <div class="mobile_menu_logo">
                <img :src="$brand.logo" :alt="$t('brand.logoAlt')" />
                <span>{{ $t('company.name') }}</span>
              </div>
              <button class="mobile_menu_close" @click="closeMobileMenu">
                <i class="el-icon-close"></i>
              </button>
            </div>
            <div class="mobile_menu_content">
              <div class="mobile_menu_item" :class="{ active: activeIndex === '1' }" @click="handleMobileMenuClick('1')">
                <i class="el-icon-house"></i>
                <span>{{ $t('navbar.Index') }}</span>
              </div>
              <div class="mobile_menu_item" :class="{ active: activeIndex === '8' }" @click="handleMobileMenuClick('8')">
                <i class="el-icon-office-building"></i>
                <span>{{ $t('navbar.Company') }}</span>
              </div>
              <div class="mobile_menu_item" :class="{ active: activeIndex === '2-1' }" @click="handleMobileMenuClick('2-1')">
                <i class="el-icon-goods"></i>
                <span>{{ $t('navbar.Products') }}</span>
              </div>
              <div class="mobile_menu_item" :class="{ active: activeIndex === '3' }" @click="handleMobileMenuClick('3')">
                <i class="el-icon-news"></i>
                <span>{{ $t('navbar.News') }}</span>
              </div>
              <div class="mobile_menu_item" :class="{ active: activeIndex === '4' }" @click="handleMobileMenuClick('4')">
                <i class="el-icon-service"></i>
                <span>{{ $t('navbar.Customer_Service') }}</span>
              </div>
              <div class="mobile_menu_item" :class="{ active: activeIndex === '7' }" @click="handleMobileMenuClick('7')">
                <i class="el-icon-question"></i>
                <span>{{ $t('navbar.FAQ') }}</span>
              </div>
              <div class="mobile_menu_item mobile_submenu">
                <div class="mobile_submenu_title" @click="toggleSubmenu('language')">
                  <i class="language-flag">{{ currentLanguageFlag }}</i>
                  {{ currentLanguageLabel }}
                  <i class="el-icon-arrow-down" :class="{ rotated: openSubmenus.language }"></i>
                </div>
                <transition name="submenu">
                  <div v-if="openSubmenus.language" class="mobile_submenu_content">
                    <div 
                      v-for="lang in availableLanguages" 
                      :key="lang.code"
                      class="mobile_submenu_item language-item"
                      :class="{ active: currentLanguage === lang.code }"
                      @click="handleMobileLanguageChange(lang.code)"
                    >
                      <i class="language-flag">{{ getLanguageFlag(lang.code) }}</i>
                      {{ lang.name }}
                    </div>
                  </div>
                </transition>
              </div>
              <div class="mobile_menu_item mobile_section_login" v-if="!isLoggedIn" @click="handleMobileLogin">
                <i class="el-icon-user"></i>
                <span>{{ $t('navbar.login') }}</span>
              </div>
              <div class="mobile_menu_item mobile_user_menu" v-else>
                <div @click="handleMobileControlCenter">
                  <i class="el-icon-s-grid"></i>
                  <span>{{ $t('navbar.controlCenter') }}</span>
                </div>
                <div class="mobile_logout_btn" @click="handleLogout">
                  <i class="el-icon-switch-button"></i>
                  <span>{{ $t('navbar.logout') }}</span>
                </div>
              </div>
              <div class="mobile_contact">
                <i class="el-icon-phone"></i>
                <span>000-000-0000</span>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { isLoggedIn as hasLoginToken, logout as clearAuth } from '@/utils/auth';

export default {
  data() {
    return {
      activeIndex: '1',
      isScrolled: false,
      isLoggedIn: false,
      isMobileMenuOpen: false,
      openSubmenus: {
        products: false,
        language: false,
      },
      // 语言相关
      currentLanguage: this.$i18n.locale,
      languageFlags: {
        'en': '🇺🇸',
        'fr': '🇫🇷', 
        'es': '🇪🇸',
        'zhCN': '🇨🇳',
        'de': '🇩🇪'
      },
    }
  },
  computed: {
    currentLanguageLabel() {
      const lang = this.availableLanguages.find(l => l.code === this.currentLanguage);
      return lang ? lang.name : 'Chinese';
    },
    currentLanguageFlag() {
      return this.languageFlags[this.currentLanguage] || '🇨🇳';
    },
    availableLanguages() {
      return [
        { code: 'en', name: this.$t('language.en') || 'English' },
        { code: 'fr', name: this.$t('language.fr') || 'French' }, 
        { code: 'es', name: this.$t('language.es') || 'Spanish' },
        { code: 'zhCN', name: this.$t('language.zh') || 'Chinese' },
        { code: 'de', name: this.$t('language.de') || 'German' }
      ]
    }
  },
  props: {
    selectIndex: {
      type: String,
      default: '',
    },
  },
  watch: {
    selectIndex: {
      immediate: true,
      handler(index) {
        this.activeIndex = index || this.resolveActiveIndex();
      },
    },
    $route() {
      this.checkLoginStatus();
      this.activeIndex = this.selectIndex || this.resolveActiveIndex();
      this.closeMobileMenu();
    },
  },
  created() {
    this.checkLoginStatus();
    this.initLanguage();
  },
  mounted() {
    this.activeIndex = this.selectIndex || this.resolveActiveIndex()
    window.addEventListener("scroll", this.handleScroll)
    window.addEventListener("storage", this.checkLoginStatus)
    window.addEventListener("focus", this.checkLoginStatus)
    this.handleScroll()
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.handleScroll)
    window.removeEventListener("storage", this.checkLoginStatus)
    window.removeEventListener("focus", this.checkLoginStatus)
    document.body.style.overflow = '';
  },
  methods: {
    resolveActiveIndex() {
      const path = this.$route.path;
      if (path.startsWith('/production')) return '2-1';
      if (path.startsWith('/news')) return '3';
      if (path.startsWith('/faq')) return '7';
      if (path.startsWith('/company')) return '8';
      if (path.startsWith('/home') || path === '/') return '1';
      return '';
    },
    // 语言相关方法
    initLanguage() {
      // 优先使用保存的语言，否则沿用 i18n 的默认语言。
      const savedLanguage = localStorage.getItem('lang') || this.$i18n.locale;
      this.currentLanguage = savedLanguage === 'zh' ? 'zhCN' : savedLanguage;
      this.$i18n.locale = this.currentLanguage;
    },

    changeLanguage(languageCode) {
      const targetLang = languageCode === 'zh' ? 'zhCN' : languageCode;
      this.currentLanguage = targetLang;
      this.$i18n.locale = targetLang;

      // 保存语言设置到localStorage
      localStorage.setItem('lang', targetLang);

      // 显示切换成功消息
      const messages = {
        'zhCN': '语言已切换为中文',
        'en': 'Language switched to English',
        'fr': 'Langue changée en français',
        'es': 'Idioma cambiado a español',
        'de': 'Sprache auf Deutsch umgestellt'
      };

      this.$message.success(messages[targetLang] || 'Language changed');
    },
    
    getLanguageFlag(code) {
      return this.languageFlags[code] || '🌐';
    },
    
    handleMobileLanguageChange(languageCode) {
      this.changeLanguage(languageCode);
      this.closeMobileMenu();
    },
    
    handleScroll() {
      this.isScrolled = window.scrollY > 10
    },
    checkLoginStatus() {
      this.isLoggedIn = hasLoginToken();
    },

    handleLogout() {
      clearAuth();
      this.isLoggedIn = false;
      this.$message.success(this.$t('navbar.logoutSuccess'));
      this.$router.push('/login').catch(() => {});
      this.closeMobileMenu();
    },

    goToHome() {
      this.$router.push('/home').catch(() => {});
    },

    // Mobile menu methods
    toggleMobileMenu() {
      this.isMobileMenuOpen = !this.isMobileMenuOpen;
      if (this.isMobileMenuOpen) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
        this.closeAllSubmenus();
      }
    },

    closeMobileMenu() {
      this.isMobileMenuOpen = false;
      document.body.style.overflow = '';
      this.closeAllSubmenus();
    },

    toggleSubmenu(submenu) {
      this.openSubmenus[submenu] = !this.openSubmenus[submenu];
    },

    closeAllSubmenus() {
      this.openSubmenus = {
        products: false,
        language: false,
      };
    },

    handleMobileMenuClick(index) {
      this.changeCurrentPage(index);
      this.closeMobileMenu();
    },

    handleMobileLogin() {
      window.location.href = '/login';
      this.closeMobileMenu();
    },

    handleMobileControlCenter() {
      window.location.href = '/controlCenter';
      this.closeMobileMenu();
    },

    goToLogin() {
      this.$router.push('/login').catch(() => {});
    },

    goToControlCenter() {
      this.$router.push('/controlCenter').catch(() => {});
    },

    changeCurrentPage(index) {
      if (!index) return;
      this.activeIndex = index
      switch (this.activeIndex) {
        case '1':
          this.$router.push('/home').catch((v) => {})
          break
        case '2-1':
          this.$router.push('/production/1').catch((v) => {})
          break
        case '2-2':
          this.$router.push('/production/2').catch((v) => {})
          break
        case '2-3':
          this.$router.push('/production/3').catch((v) => {})
          break
        case '2-4':
          this.$router.push('/production/4').catch((v) => {})
          break
        case '3':
          this.$router.push('/news').catch((v) => {})
          break
        case '7':
          this.$router.push('/faq').catch((v) => {})
          break
        case '8':
          this.$router.push('/company').catch((v) => {})
          break
        case '4-1':
          this.$router.push('/fournav/1').catch((v) => {})
          break
        case '4-2':
          this.$router.push('/fournav/2').catch((v) => {})
          break
        case '4-3':
          this.$router.push('/fournav/3').catch((v) => {})
          break
        case '99':
          // 切换中文
this.$i18n.locale = 'zhCN'
localStorage.setItem('lang', 'zhCN')
          break
        case '98':
          // 切换英文
this.$i18n.locale = 'en'
localStorage.setItem('lang', 'en')
          break
          case '97':
          // 切换德语
this.$i18n.locale = 'de'
localStorage.setItem('lang', 'de')
          break
        // case '5-3':
        //   this.$router.push('/fivenav/3').catch((v) => {})
        //   break
        // case '5-4':
        //   this.$router.push('/fivenav/2').catch((v) => {})
        //   break
        default:
          break
      }
    },
  },
}
</script>

<style lang="scss" scoped>
// Root component wrapper
.navbar_component {
  position: relative;
  z-index: 1000;
}

// Desktop Navbar Styles
.navbar_wrap {
  width: 100%;
  height: 70px;
  background: rgba(10, 127, 215, 0.05); /* Very light blue tint matching #0a7fd7 */
  backdrop-filter: saturate(180%) blur(5px);
  display: flex;
  align-items: center;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  transition: all 0.4s ease;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  &.scrolled {
    background: linear-gradient(134deg, #0649b8 1%, #0a7fd7 41%, #03d2c5 99%); /* New Gradient Blue */
    backdrop-filter: none;
    box-shadow: 0 4px 20px rgba(10, 127, 215, 0.3);
    border-bottom: 1px solid transparent;

    .comName {
      background: linear-gradient(134deg, #fff 0%, #03d2c5 100%);
      background-clip: text;
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }
    .right_phone {
      color: #fff;
    }

    .auth_actions {
      .auth_btn--control {
        color: #fff;
        border-color: rgba(255, 255, 255, 0.6);

        &:hover {
          background: rgba(255, 255, 255, 0.12);
          border-color: #fff;
        }
      }

      .auth_btn--logout {
        color: rgba(255, 255, 255, 0.9);
        border-color: rgba(255, 255, 255, 0.5);

        &:hover {
          color: #fff;
          background: rgba(255, 255, 255, 0.12);
          border-color: #fff;
        }
      }
    }

    ::v-deep .el-menu--horizontal > .el-menu-item,
    ::v-deep .el-menu--horizontal > .el-submenu .el-submenu__title {
      color: rgba(255, 255, 255, 0.9) !important;
      &:hover {
        color: #fff !important;
        font-weight: 600;
        background-color: rgba(255, 255, 255, 0.1) !important;
        transform: translateY(-2px);
      }
    }
    ::v-deep .el-menu--horizontal > .el-menu-item.is-active,
    ::v-deep .el-menu--horizontal > .el-submenu.is-active > .el-submenu__title {
      color: #fff !important;
      border-bottom: 2px solid #fff !important;
      font-weight: bold;
    }
  }

  .navbar_container {
    // width: min(90%, 1200px);
    height: 100%;
    margin: 0 auto;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 20px;

    @media (max-width: 1200px) {
      width: 95%;
      padding: 0 15px;
    }

    .navbar_left {
      display: flex;
      align-items: center;
      height: 100%;
      cursor: pointer;
      img {
        width: 36px;
        height: 36px;
        margin-right: 12px;
        transition: transform 0.3s ease;
        &:hover {
          transform: scale(1.05);
        }
      }
    }
    .navbar_right {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      font-size: 16px;
      gap: 20px;

      a {
        text-decoration: none;
      }

      .auth_actions {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-left: 4px;

        .auth_btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 7px;
          height: 40px;
          padding: 0 18px;
          border-radius: 8px;
          font-size: 15px;
          font-weight: 600;
          line-height: 1;
          cursor: pointer;
          white-space: nowrap;
          transition: all 0.25s ease;
          -webkit-tap-highlight-color: transparent;

          i {
            font-size: 16px;
          }

          &:active {
            transform: translateY(0) scale(0.98);
          }
        }

        .auth_btn--login {
          background: linear-gradient(134deg, #0649b8 1%, #0a7fd7 41%, #03d2c5 99%);
          color: #fff;
          border: none;
          box-shadow: 0 2px 8px rgba(10, 127, 215, 0.25);

          &:hover {
            transform: translateY(-1px);
            box-shadow: 0 6px 16px rgba(10, 127, 215, 0.35);
          }
        }

        .auth_btn--control {
          background: transparent;
          border: 1px solid rgba(10, 127, 215, 0.35);
          color: #ffffff;

          &:hover {
            background: rgba(10, 127, 215, 0.08);
            border-color: #0a7fd7;
            transform: translateY(-1px);
          }
        }

        .auth_btn--logout {
          background: transparent;
          border: 1px solid #e2e8f0;
          color: #64748b;

          &:hover {
            color: #e11d48;
            border-color: rgba(225, 29, 72, 0.4);
            background: rgba(225, 29, 72, 0.06);
            transform: translateY(-1px);
          }
        }
      }
      .el_menu_demo {
        background-color: transparent !important;
        border: none;

        ::v-deep .el-menu-item,
        ::v-deep .el-submenu__title {
          color: #1e293b; /* Dark text for un-scrolled light header */
          background-color: transparent !important;
          transition: transform 0.3s ease-out, color 0.3s;
          font-size: 16px;
          font-weight: 500;
          &:hover {
            color: #0a7fd7; /* Link hover */
            background-color: transparent !important;
            transform: translateY(-2px);
          }
        }

        ::v-deep .el-menu-item.is-active,
        ::v-deep .el-submenu.is-active .el-submenu__title {
          border-bottom: 2px solid #0a7fd7 !important;
          font-weight: 700;
        }

        /* Update Element UI's default submenu background colors on hover */
        ::v-deep .el-menu--popup {
          background-color: #ffffff;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          border-radius: 6px;
          border: 1px solid #e2e8f0;

          .el-menu-item {
            color: #1e293b !important;
            &:hover {
              background-color: rgba(10, 127, 215, 0.1) !important;
              color: #0a7fd7 !important;
              transform: none; /* remove translateY from global */
            }
          }
        }
      }
      .menu_item {
        &:hover {
          background: transparent !important;
        }
      }
      .right_phone {
        display: flex;
        align-items: center;
        color: #1e293b; /* Dark initial color */
        transition: all 0.3s;
        cursor: pointer;
        font-weight: 500;

        &:hover {
          color: #0a7fd7;
          transform: translateY(-1px);
        }

        i {
          font-size: 18px;
          margin-right: 8px;
        }

        span {
          font-size: 14px;
        }
      }
    }
  }
  .comName {
    font-size: 20px;
    font-weight: 800;
    background: linear-gradient(134deg, #0649b8 0%, #0a7fd7 100%);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    transition: all 0.3s;
    letter-spacing: -0.5px;
  }
}

// Mobile Navbar Styles
.mobile_navbar {
  display: none;
  width: 100%;
  height: 60px;
  background: linear-gradient(135deg, rgba(6, 73, 184, 0.96) 0%, rgba(10, 127, 215, 0.92) 52%, rgba(3, 210, 197, 0.88) 100%);
  backdrop-filter: saturate(180%) blur(5px);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  transition: all 0.4s ease;

  &.scrolled {
    box-shadow: 0 6px 20px rgba(6, 73, 184, 0.32);
  }

  .mobile_navbar_container {
    width: 100%;
    height: 100%;
    padding: 0 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .mobile_navbar_left {
      display: flex;
      align-items: center;
      cursor: pointer;
      
      img {
        width: 36px;
        height: 36px;
        margin-right: 12px;
      }
      
      .mobile_comName {
        font-size: 18px;
        font-weight: 800;
        background: linear-gradient(135deg, #ffffff 0%, rgba(255, 255, 255, 0.86) 100%);
        background-clip: text;
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        transition: all 0.3s;
      }
    }

    .mobile_navbar_right {
      .hamburger_btn {
        background: none;
        border: none;
        cursor: pointer;
        padding: 8px;
        display: flex;
        flex-direction: column;
        justify-content: space-around;
        width: 30px;
        height: 30px;

        span {
          display: block;
          height: 3px;
          width: 100%;
          background-color: #ffffff; /* White lines on gradient header */
          border-radius: 2px;
          transition: all 0.3s ease;
        }

        &.active {
          span:nth-child(1) {
            transform: rotate(45deg) translate(5px, 5px);
          }
          span:nth-child(2) {
            opacity: 0;
          }
          span:nth-child(3) {
            transform: rotate(-45deg) translate(7px, -6px);
          }
        }
      }
    }
  }
}

// Mobile Menu Overlay
.mobile_menu_overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  height: 100dvh;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1001;
  display: flex;
  justify-content: flex-end;

  .mobile_menu {
    width: min(86vw, 360px);
    height: 100%;
    background: linear-gradient(135deg, rgba(6, 73, 184, 0.96) 0%, rgba(10, 127, 215, 0.92) 52%, rgba(3, 210, 197, 0.88) 100%);
    box-shadow: -5px 0 20px rgba(0, 0, 0, 0.3);
    display: flex;
    flex-direction: column;

    .mobile_menu_header {
      padding: max(18px, env(safe-area-inset-top)) 18px 16px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.2);
      display: flex;
      justify-content: space-between;
      align-items: center;

      .mobile_menu_logo {
        display: flex;
        align-items: center;
        
        img {
          width: 40px;
          height: 40px;
          margin-right: 12px;
        }
        
        span {
          color: #fff;
          font-size: 16px;
          font-weight: 600;
        }
      }

      .mobile_menu_close {
        background: none;
        border: none;
        color: #fff;
        font-size: 24px;
        cursor: pointer;
        padding: 8px;
        border-radius: 50%;
        transition: background-color 0.2s ease;
        -webkit-tap-highlight-color: transparent;
        
        &:hover {
          background-color: rgba(255, 255, 255, 0.2);
        }

        &:active {
          background-color: rgba(255, 255, 255, 0.3);
        }
      }
    }

    .mobile_menu_content {
      flex: 1;
      padding: 14px 0 max(18px, env(safe-area-inset-bottom));
      overflow-y: auto;

      .mobile_menu_item {
        display: flex;
        align-items: center;
        gap: 12px;
        min-height: 48px;
        padding: 14px 20px;
        color: #fff;
        font-size: 16px;
        cursor: pointer;
        transition: background-color 0.2s ease, color 0.2s ease;
        border-left: 3px solid transparent;
        -webkit-tap-highlight-color: transparent;

        > i {
          width: 20px;
          text-align: center;
          color: rgba(255, 255, 255, 0.72);
          transition: color 0.2s ease;
        }

        &:active {
          background-color: rgba(255, 255, 255, 0.08);
        }

        &.active {
          background: rgba(255, 255, 255, 0.16);
          border-left-color: #5eead4;

          > i {
            color: #ffffff;
          }
        }
      }

      .mobile_submenu {
        .mobile_submenu_title {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 12px;

          .language-flag {
            width: 20px;
            flex: 0 0 20px;
            margin-right: 0;
            text-align: center;
          }
          
          i {
            transition: transform 0.3s;
            &.rotated {
              transform: rotate(180deg);
            }
          }
        }

        .mobile_submenu_content {
          background-color: rgba(0, 0, 0, 0.2);
          
          .mobile_submenu_item {
            display: flex;
            align-items: center;
            padding: 12px 40px;
            font-size: 14px;
            transition: background-color 0.2s ease;
            
            &:active {
              background-color: rgba(255, 255, 255, 0.1);
            }
          }
        }
      }

      .mobile_section_login {
        margin-top: 6px;
        border-top: 1px solid rgba(255, 255, 255, 0.1);
      }

      .mobile_user_menu {
        display: flex;
        flex-direction: column;
        gap: 0;
        padding: 0;
        margin-top: 6px;
        border-top: 1px solid rgba(255, 255, 255, 0.1);
        
        > div {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 15px 20px;
          cursor: pointer;
          transition: background-color 0.2s ease;
          -webkit-tap-highlight-color: transparent;
          
          i {
            width: 20px;
            text-align: center;
            color: rgba(255, 255, 255, 0.72);
          }

          &:active {
            background-color: rgba(255, 255, 255, 0.08);
          }
        }
        
        .mobile_logout_btn {
          justify-content: center;
          gap: 8px;
          background-color: rgba(255, 255, 255, 0.14);
          margin: 10px 20px 0;
          border-radius: 8px;
          
          i {
            width: auto;
          }

          &:active {
            background-color: rgba(255, 255, 255, 0.24);
          }
        }
      }

      .mobile_contact {
        margin-top: auto;
        padding: 20px;
        border-top: 1px solid rgba(255, 255, 255, 0.1);
        background: rgba(255, 255, 255, 0.05);
        display: flex;
        align-items: center;
        color: #fff;
        
        i {
          font-size: 18px;
          margin-right: 12px;
          color: #5eead4;
        }
        
        span {
          font-size: 14px;
        }
      }
    }
  }
}

@media (max-width: 991px) {
  .navbar_wrap {
    display: none;
  }

  .mobile_navbar {
    display: block;
  }
}

@media (max-width: 420px) {
  .mobile_navbar {
    height: 56px;

    .mobile_navbar_container {
      padding: 0 14px;

      .mobile_navbar_left {
        min-width: 0;

        img {
          width: 32px;
          height: 32px;
          margin-right: 8px;
        }

        .mobile_comName {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          font-size: 16px;
        }
      }
    }
  }

  .mobile_menu_overlay .mobile_menu {
    width: min(90vw, 340px);
  }
}

// Transitions
.mobile-menu-enter-active,
.mobile-menu-leave-active {
  transition: opacity 0.3s ease;
  
  .mobile_menu {
    transition: transform 0.3s ease;
  }
}

.mobile-menu-enter,
.mobile-menu-leave-to {
  opacity: 0;
  
  .mobile_menu {
    transform: translateX(100%);
  }
}

.submenu-enter-active,
.submenu-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}

.submenu-enter,
.submenu-leave-to {
  max-height: 0;
  opacity: 0;
}

.submenu-enter-to,
.submenu-leave {
  max-height: 200px;
  opacity: 1;
}

// Language Selector Styles
.language-flag {
  font-style: normal;
  margin-right: 8px;
}

.language-item {
  &.active {
    background-color: rgba(10, 127, 215, 0.1) !important;
    color: #0a7fd7 !important;
    font-weight: 600;
  }
}

.mobile_submenu_item.language-item {
  display: flex;
  align-items: center;
  
  &.active {
    background-color: rgba(255, 255, 255, 0.2);
    font-weight: 600;
  }
}

// Responsive breakpoints
@media (max-width: 768px) {
  .navbar_wrap .navbar_container {
    padding: 0 10px;
  }
  
  .mobile_navbar .mobile_navbar_container {
    padding: 0 15px;
  }
  
  .mobile_menu {
    width: 90% !important;
  }
}

@media (max-width: 480px) {
  .mobile_menu {
    width: 95% !important;
  }
  
  .mobile_comName {
    font-size: 14px !important;
  }
}
</style>

<style>
/* Global overrides for Element UI dropdown menus since they append to body */
.el-menu--popup {
  background-color: #ffffff !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
  border-radius: 6px !important;
  border: 1px solid #e2e8f0 !important;
  padding: 5px 0 !important;
}

.el-menu--popup .el-menu-item {
  color: #1e293b !important;
  transition: all 0.2s ease !important;
}

.el-menu--popup .el-menu-item:hover,
.el-menu--popup .el-menu-item.is-active,
.el-menu--popup .el-menu-item.active {
  background-color: rgba(10, 127, 215, 0.08) !important;
  color: #0a7fd7 !important;
  font-weight: 600 !important;
}

/* Mobile navbar rem overrides: px-to-viewport makes px too small on phones. */
@media (max-width: 991px) {
  .navbar_component .mobile_navbar {
    height: 3.75rem !important;
  }

  .navbar_component .mobile_navbar .mobile_navbar_container {
    padding: 0 1.25rem !important;
  }

  .navbar_component .mobile_navbar .mobile_navbar_left {
    min-width: 0 !important;
  }

  .navbar_component .mobile_navbar .mobile_navbar_left img {
    width: 2.25rem !important;
    height: 2.25rem !important;
    margin-right: 0.75rem !important;
    flex: 0 0 auto !important;
  }

  .navbar_component .mobile_navbar .mobile_comName {
    min-width: 0 !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    white-space: nowrap !important;
    font-size: 1.125rem !important;
    line-height: 1.2 !important;
  }

  .navbar_component .mobile_navbar .hamburger_btn {
    width: 2.75rem !important;
    height: 2.75rem !important;
    padding: 0.625rem !important;
  }

  .navbar_component .mobile_navbar .hamburger_btn span {
    height: 0.1875rem !important;
  }

  .navbar_component .mobile_menu_overlay {
    height: 100vh !important;
    height: 100dvh !important;
  }

  .navbar_component .mobile_menu_overlay .mobile_menu {
    width: min(86vw, 22.5rem) !important;
    max-width: none !important;
  }

  .navbar_component .mobile_menu_header {
    padding: max(1.125rem, env(safe-area-inset-top)) 1.125rem 1rem !important;
    border-bottom-width: 0.0625rem !important;
  }

  .navbar_component .mobile_menu_logo img {
    width: 2.5rem !important;
    height: 2.5rem !important;
    margin-right: 0.75rem !important;
  }

  .navbar_component .mobile_menu_logo span {
    font-size: 1rem !important;
    line-height: 1.25 !important;
  }

  .navbar_component .mobile_menu_close {
    min-width: 2.5rem !important;
    min-height: 2.5rem !important;
    padding: 0.5rem !important;
    font-size: 1.5rem !important;
  }

  .navbar_component .mobile_menu_content {
    padding: 0.875rem 0 max(1.125rem, env(safe-area-inset-bottom)) !important;
  }

  .navbar_component .mobile_menu_content .mobile_menu_item,
  .navbar_component .mobile_user_menu > div {
    min-height: 3rem !important;
    padding: 0.875rem 1.25rem !important;
    font-size: 1rem !important;
    line-height: 1.35 !important;
  }

  .navbar_component .mobile_submenu_content .mobile_submenu_item {
    padding: 0.75rem 2.5rem !important;
    font-size: 0.9375rem !important;
    min-height: 2.75rem !important;
  }

  .navbar_component .mobile_contact {
    padding: 1rem 1.25rem !important;
    border-top-width: 0.0625rem !important;
  }

  .navbar_component .mobile_contact i {
    margin-right: 0.75rem !important;
    font-size: 1.125rem !important;
  }

  .navbar_component .mobile_contact span {
    font-size: 0.875rem !important;
  }
}

@media (max-width: 420px) {
  .navbar_component .mobile_navbar {
    height: 3.5rem !important;
  }

  .navbar_component .mobile_navbar .mobile_navbar_container {
    padding: 0 0.875rem !important;
  }

  .navbar_component .mobile_navbar .mobile_navbar_left img {
    width: 2rem !important;
    height: 2rem !important;
    margin-right: 0.5rem !important;
  }

  .navbar_component .mobile_navbar .mobile_comName {
    font-size: 1rem !important;
  }

  .navbar_component .mobile_menu_overlay .mobile_menu {
    width: min(90vw, 21.25rem) !important;
  }
}
</style>
